/**
 * <mdn-loop>: cinta infinita que avanza sola y que el cliente puede arrastrar
 * con el dedo o el ratón (hacia delante o hacia atrás). Al soltar sigue con impulso
 * y, tras una pausa corta, vuelve a moverse sola.
 *
 * Marcado:
 * <mdn-loop speed="40" direction="left|right" pause-on-hover>
 *   <div data-loop-viewport><ul data-loop-track>…</ul></div>
 * </mdn-loop>
 *
 * Ojo: no usar nombres de propiedades que ya existen en HTMLElement (dir, hidden, title…).
 */
class MdnLoop extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-loop-viewport]');
    this.track = this.querySelector('[data-loop-track]');
    if (!this.viewport || !this.track || !this.track.children.length) return;
    this.speed = Math.max(0, Number(this.getAttribute('speed')) || 40);
    this.sign = this.getAttribute('direction') === 'right' ? 1 : -1;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.offset = this.offset || 0;
    this.vel = 0;
    this.hold = 0;
    this.hover = false;
    this.visible = true;
    this.drag = null;
    this.last = 0;
    this.suppressUntil = 0;
    this.ac = new AbortController();
    this.loop = (time) => this.step(time);
    this.build();
    this.bind();
    this.classList.add('is-live');
    this.raf = requestAnimationFrame(this.loop);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.raf);
    if (this.ac) this.ac.abort();
    if (this.io) this.io.disconnect();
  }

  makeClone(node) {
    const copy = node.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    copy.querySelectorAll('a, button, input').forEach((el) => el.setAttribute('tabindex', '-1'));
    return copy;
  }

  build() {
    const track = this.track;
    if (!track.dataset.built) {
      const base = Array.from(track.children);
      const need = Math.max(window.innerWidth, 1280) + 240;
      let width = track.scrollWidth;
      let guard = 0;
      while (width > 0 && width < need && guard < 30) {
        base.forEach((node) => track.appendChild(this.makeClone(node)));
        width = track.scrollWidth;
        guard += 1;
      }
      Array.from(track.children).forEach((node) => track.appendChild(this.makeClone(node)));
      track.dataset.built = 'true';
    }
    this.measure();
  }

  measure() {
    this.W = this.track.scrollWidth / 2;
    this.wrap();
    this.apply();
  }

  pause(ms) {
    this.hold = Math.max(this.hold, performance.now() + ms);
  }

  /** Mueve la cinta un tramo: step 1 = siguiente, -1 = anterior. */
  nudge(step) {
    if (!this.W) return;
    this.vel = -step * 3 * Math.max(160, this.viewport.clientWidth * 0.6);
    this.pause(2500);
  }

  wrap() {
    const W = this.W;
    if (!W) return;
    if (this.offset <= -W || this.offset > 0) {
      this.offset %= W;
      if (this.offset > 0) this.offset -= W;
    }
  }

  apply() {
    this.track.style.transform = `translate3d(${this.offset.toFixed(2)}px, 0, 0)`;
  }

  step(time) {
    this.raf = requestAnimationFrame(this.loop);
    const dt = this.last ? Math.min(0.05, (time - this.last) / 1000) : 0;
    this.last = time;
    if (!this.W || !this.visible || this.drag || !dt) return;
    if (Math.abs(this.vel) > 5) {
      this.offset += this.vel * dt;
      this.vel *= Math.pow(0.05, dt);
    } else {
      this.vel = 0;
      if (this.reduced || this.hover || time < this.hold) return;
      this.offset += this.sign * this.speed * dt;
    }
    this.wrap();
    this.apply();
  }

  bind() {
    const signal = this.ac.signal;
    const vp = this.viewport;

    vp.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.vel = 0;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, start: this.offset, lastX: event.clientX, lastT: event.timeStamp, v: 0, moved: false };
    }, { signal });

    vp.addEventListener('pointermove', (event) => {
      const d = this.drag;
      if (!d || event.pointerId !== d.id) return;
      const dx = event.clientX - d.x;
      if (!d.moved) {
        if (Math.abs(dx) < 6) return;
        if (Math.abs(event.clientY - d.y) > Math.abs(dx)) {
          this.drag = null;
          this.pause(1200);
          return;
        }
        d.moved = true;
        try {
          vp.setPointerCapture(event.pointerId);
        } catch (error) {
          /* sin captura: el arrastre sigue funcionando dentro de la cinta */
        }
        this.classList.add('is-dragging');
      }
      const elapsed = Math.max(1, event.timeStamp - d.lastT);
      d.v = 0.8 * ((event.clientX - d.lastX) / elapsed) * 1000 + 0.2 * d.v;
      d.lastX = event.clientX;
      d.lastT = event.timeStamp;
      this.offset = d.start + dx;
      this.wrap();
      this.apply();
    }, { signal });

    const end = (event) => {
      const d = this.drag;
      if (!d || event.pointerId !== d.id) return;
      this.drag = null;
      this.last = 0;
      if (d.moved) {
        this.classList.remove('is-dragging');
        const recent = event.timeStamp - d.lastT < 100;
        this.vel = recent ? Math.max(-4000, Math.min(4000, d.v)) : 0;
        this.suppressUntil = performance.now() + 350;
      }
      this.pause(2000);
    };
    vp.addEventListener('pointerup', end, { signal });
    vp.addEventListener('pointercancel', end, { signal });
    vp.addEventListener('pointerleave', (event) => {
      if (this.drag && !this.drag.moved && event.pointerId === this.drag.id) this.drag = null;
    }, { signal });

    vp.addEventListener('click', (event) => {
      if (performance.now() < this.suppressUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, { capture: true, signal });
    vp.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    vp.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      this.vel = 0;
      this.offset -= event.deltaX;
      this.wrap();
      this.apply();
      this.pause(2000);
    }, { passive: false, signal });

    if (this.hasAttribute('pause-on-hover') && window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('mouseenter', () => { this.hover = true; }, { signal });
      this.addEventListener('mouseleave', () => { this.hover = false; }, { signal });
    }
    this.addEventListener('focusin', () => { this.hover = true; }, { signal });
    this.addEventListener('focusout', () => { this.hover = false; }, { signal });

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.last = 0;
      });
      this.io.observe(this);
    }
    window.addEventListener('resize', () => this.measure(), { signal });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.measure());
  }
}

if (!customElements.get('mdn-loop')) customElements.define('mdn-loop', MdnLoop);